import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, FileText } from 'lucide-react';
import AppLogo from '@/components/AppLogo';

const TermsOfServicePage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-white/80 backdrop-blur-sm border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="shrink-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <AppLogo size="sm" />
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 bg-purple-100 rounded-xl">
            <FileText className="h-6 w-6 text-purple-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Terms of Service</h1>
            <p className="text-sm text-slate-500">Effective Date: February 18, 2026</p>
          </div>
        </div>

        <div className="prose prose-slate max-w-none">
          <p className="lead text-slate-600">
            Welcome to For Get Me Not. These Terms of Service ("Terms") govern your access to and use of the For Get Me Not mobile application and related services (collectively, the "Service") provided by For Get Me Not ("we," "us," or "our").
          </p>
          <p className="text-slate-600">
            By accessing or using the Service, you agree to be bound by these Terms. If you do not agree to these Terms, do not use the Service.
          </p>

          <hr className="my-8 border-slate-200" />

          <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">1. Description of Service</h2>
          <p className="text-slate-600">
            For Get Me Not is a personal organization and life management application designed to help you track and manage various aspects of your daily life, including but not limited to:
          </p>
          <ul className="list-disc pl-6 text-slate-600 space-y-2">
            <li>Documents and important files</li>
            <li>Receipts and financial records</li>
            <li>Medical notes and medication tracking</li>
            <li>Contacts and relationships</li>
            <li>Vehicle maintenance and warranty information</li>
            <li>Appointments, birthdays, and important dates</li>
            <li>Shopping lists and household management</li>
          </ul>
          <p className="text-slate-600 mt-4">
            The Service is provided on a subscription basis and is intended for personal, non-commercial use.
          </p>

          <hr className="my-8 border-slate-200" />

          <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">2. No Professional Advice Disclaimer</h2>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 my-6">
            <p className="text-amber-800 font-medium mb-3">IMPORTANT DISCLAIMER</p>
            <p className="text-amber-700 mb-3">
              The Service is a personal organization tool only. For Get Me Not does NOT provide and the Service should NOT be relied upon for:
            </p>
            <ul className="list-disc pl-6 text-amber-700 space-y-2">
              <li><strong>Medical Advice:</strong> Any health-related features are for personal note-taking only. Always consult qualified healthcare professionals for medical advice, diagnosis, or treatment.</li>
              <li><strong>Legal Advice:</strong> Document storage features do not constitute legal services. Consult a licensed attorney for legal matters.</li>
              <li><strong>Financial Advice:</strong> Receipt and expense tracking features are for personal record-keeping only. Consult qualified financial advisors for financial decisions.</li>
              <li><strong>Mechanical Advice:</strong> Vehicle maintenance reminders are for personal tracking only. Consult qualified mechanics for vehicle repairs and maintenance decisions.</li>
            </ul>
          </div>

          <hr className="my-8 border-slate-200" />

          <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">3. Health Information Disclaimer (HIPAA)</h2>
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 my-6">
            <p className="text-red-800 font-medium mb-3">HIPAA DISCLAIMER</p>
            <ul className="list-disc pl-6 text-red-700 space-y-2">
              <li>For Get Me Not is <strong>NOT a healthcare provider</strong></li>
              <li>The Service is <strong>NOT HIPAA compliant</strong> and is not designed to store Protected Health Information (PHI)</li>
              <li>Any medical notes, medication records, or health-related information you store in the app is done <strong>entirely at your own risk</strong></li>
              <li>The Service is <strong>NOT a substitute</strong> for professional medical record systems, electronic health records (EHR), patient portals, or healthcare provider documentation</li>
              <li>You acknowledge and accept full responsibility for any health information you choose to store</li>
              <li><strong>Do not use this Service to store information requiring HIPAA-level protection</strong></li>
            </ul>
          </div>

          <hr className="my-8 border-slate-200" />

          <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">4. Account Registration and Security</h2>
          <h3 className="text-lg font-medium text-slate-800 mt-6 mb-3">4.1 Account Creation</h3>
          <p className="text-slate-600">
            To use the Service, you must create an account by providing accurate, current, and complete information. You agree to update your information as necessary to maintain its accuracy.
          </p>

          <h3 className="text-lg font-medium text-slate-800 mt-6 mb-3">4.2 Account Security</h3>
          <p className="text-slate-600">
            You are responsible for:
          </p>
          <ul className="list-disc pl-6 text-slate-600 space-y-2">
            <li>Maintaining the confidentiality of your account credentials</li>
            <li>All activities that occur under your account</li>
            <li>Notifying us immediately of any unauthorized access or security breach</li>
            <li>Using strong, unique passwords and keeping them secure</li>
          </ul>
          <p className="text-slate-600 mt-4">
            We will not be liable for any loss or damage arising from your failure to protect your account credentials.
          </p>

          <hr className="my-8 border-slate-200" />

          <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">5. User Content</h2>
          <h3 className="text-lg font-medium text-slate-800 mt-6 mb-3">5.1 Ownership</h3>
          <p className="text-slate-600">
            You retain full ownership of all content, documents, images, and information you upload, create, or store through the Service ("User Content"). We do not claim any ownership rights to your User Content.
          </p>

          <h3 className="text-lg font-medium text-slate-800 mt-6 mb-3">5.2 License Grant</h3>
          <p className="text-slate-600">
            By uploading User Content, you grant us a limited, non-exclusive license to store, process, and display your User Content solely for the purpose of providing the Service to you. This license terminates when you delete your User Content or your account.
          </p>

          <h3 className="text-lg font-medium text-slate-800 mt-6 mb-3">5.3 Your Responsibility</h3>
          <p className="text-slate-600">
            You are solely responsible for your User Content and represent that you have all necessary rights to upload and store such content. You agree not to upload content that is illegal, harmful, or infringes on others' rights.
          </p>

          <hr className="my-8 border-slate-200" />

          <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">6. Acceptable Use Policy</h2>
          <p className="text-slate-600">You agree NOT to use the Service to:</p>
          <ul className="list-disc pl-6 text-slate-600 space-y-2">
            <li>Violate any applicable laws, regulations, or third-party rights</li>
            <li>Upload malicious software, viruses, or harmful code</li>
            <li>Attempt to gain unauthorized access to the Service or other users' accounts</li>
            <li>Interfere with or disrupt the Service's operation</li>
            <li>Use automated systems to access the Service without permission</li>
            <li>Store content that is illegal, defamatory, or infringes intellectual property rights</li>
            <li>Use the Service for any commercial purpose without authorization</li>
            <li>Impersonate any person or entity</li>
          </ul>

          <hr className="my-8 border-slate-200" />

          <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">7. Subscription and Billing</h2>
          <h3 className="text-lg font-medium text-slate-800 mt-6 mb-3">7.1 Subscription Plans</h3>
          <p className="text-slate-600">
            The Service is offered on a subscription basis. Details of available subscription plans, including pricing and features, are available within the app and on our website.
          </p>

          <h3 className="text-lg font-medium text-slate-800 mt-6 mb-3">7.2 Payment and Renewal</h3>
          <ul className="list-disc pl-6 text-slate-600 space-y-2">
            <li>Subscriptions are billed in advance on a recurring basis (monthly or annually)</li>
            <li><strong>Auto-Renewal:</strong> Your subscription will automatically renew at the end of each billing period unless you cancel before the renewal date</li>
            <li>Payment is processed through the Apple App Store or Google Play Store</li>
            <li>Prices may change with reasonable notice; continued use after a price change constitutes acceptance</li>
          </ul>

          <h3 className="text-lg font-medium text-slate-800 mt-6 mb-3">7.3 Cancellation</h3>
          <p className="text-slate-600">
            You may cancel your subscription at any time through your Apple App Store or Google Play Store account settings. Cancellation will take effect at the end of your current billing period. You will retain access to the Service until then.
          </p>

          <h3 className="text-lg font-medium text-slate-800 mt-6 mb-3">7.4 Refunds</h3>
          <p className="text-slate-600">
            Refund requests are handled by Apple or Google according to their respective refund policies. We do not process refunds directly.
          </p>

          <hr className="my-8 border-slate-200" />

          <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">8. Data Backup Responsibility</h2>
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 my-6">
            <p className="text-blue-800 font-medium mb-2">Important Notice</p>
            <p className="text-blue-700">
              While we implement measures to protect your data, <strong>you are responsible for maintaining your own backups</strong> of important information. We recommend periodically exporting critical data. We are not responsible for any loss of data due to service interruptions, technical failures, or account termination.
            </p>
          </div>

          <hr className="my-8 border-slate-200" />

          <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">9. Limitation of Liability</h2>
          <p className="text-slate-600">
            TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW:
          </p>
          <ul className="list-disc pl-6 text-slate-600 space-y-2">
            <li>THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED</li>
            <li>WE DISCLAIM ALL WARRANTIES, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT</li>
            <li>WE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES</li>
            <li>OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID FOR THE SERVICE IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM</li>
            <li>WE ARE NOT LIABLE FOR ANY LOSS OR DAMAGE RESULTING FROM YOUR RELIANCE ON INFORMATION STORED IN THE SERVICE</li>
          </ul>

          <hr className="my-8 border-slate-200" />

          <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">10. Indemnification</h2>
          <p className="text-slate-600">
            You agree to indemnify, defend, and hold harmless For Get Me Not and its officers, directors, employees, and agents from any claims, damages, losses, liabilities, and expenses (including reasonable attorneys' fees) arising out of or related to your use of the Service, your User Content, or your violation of these Terms.
          </p>

          <hr className="my-8 border-slate-200" />

          <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">11. Termination</h2>
          <h3 className="text-lg font-medium text-slate-800 mt-6 mb-3">11.1 Termination by You</h3>
          <p className="text-slate-600">
            You may terminate your account at any time by canceling your subscription and deleting your account through the app settings.
          </p>

          <h3 className="text-lg font-medium text-slate-800 mt-6 mb-3">11.2 Termination by Us</h3>
          <p className="text-slate-600">
            We may suspend or terminate your access to the Service at any time, with or without cause, with or without notice, including for:
          </p>
          <ul className="list-disc pl-6 text-slate-600 space-y-2">
            <li>Violation of these Terms</li>
            <li>Non-payment of subscription fees</li>
            <li>Conduct that we determine is harmful to other users or the Service</li>
            <li>Legal requirements or requests from law enforcement</li>
          </ul>

          <h3 className="text-lg font-medium text-slate-800 mt-6 mb-3">11.3 Effect of Termination</h3>
          <p className="text-slate-600">
            Upon termination, your right to use the Service will immediately cease. We may delete your User Content within 30 days of termination. Provisions of these Terms that by their nature should survive termination will survive.
          </p>

          <hr className="my-8 border-slate-200" />

          <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">12. Changes to Terms</h2>
          <p className="text-slate-600">
            We reserve the right to modify these Terms at any time. We will provide notice of material changes by posting the updated Terms in the app and updating the "Effective Date." Your continued use of the Service after changes become effective constitutes your acceptance of the revised Terms. If you do not agree to the changes, you must stop using the Service.
          </p>

          <hr className="my-8 border-slate-200" />

          <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">13. Governing Law and Dispute Resolution</h2>
          <p className="text-slate-600">
            These Terms shall be governed by and construed in accordance with the laws of the United States and the State of Delaware, without regard to conflict of law principles. Any disputes arising out of or relating to these Terms or the Service shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association, except that either party may seek injunctive relief in any court of competent jurisdiction.
          </p>

          <hr className="my-8 border-slate-200" />

          <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">14. General Provisions</h2>
          <ul className="list-disc pl-6 text-slate-600 space-y-2">
            <li><strong>Entire Agreement:</strong> These Terms, together with our Privacy Policy, constitute the entire agreement between you and For Get Me Not regarding the Service.</li>
            <li><strong>Severability:</strong> If any provision of these Terms is found unenforceable, the remaining provisions will continue in effect.</li>
            <li><strong>Waiver:</strong> Our failure to enforce any provision of these Terms does not constitute a waiver of that provision.</li>
            <li><strong>Assignment:</strong> You may not assign your rights under these Terms without our written consent. We may assign our rights without restriction.</li>
          </ul>

          <hr className="my-8 border-slate-200" />

          <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">15. Contact Us</h2>
          <p className="text-slate-600">
            If you have any questions about these Terms of Service, please contact us at:
          </p>
          <div className="bg-slate-100 rounded-xl p-4 my-4">
            <p className="text-slate-700">
              <strong>For Get Me Not</strong><br />
              Email: <a href="mailto:legal@forgetmenot.app" className="text-blue-600 hover:underline">legal@forgetmenot.app</a><br />
              Subject Line: Terms of Service Inquiry
            </p>
          </div>

          <hr className="my-8 border-slate-200" />

          <p className="text-sm text-slate-500 mt-8">
            Last Updated: February 18, 2026
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-100 mt-12">
        <div className="max-w-4xl mx-auto px-4 py-6 text-center text-sm text-slate-500">
          <p>© 2026 For Get Me Not. All rights reserved.</p>
          <div className="flex justify-center gap-4 mt-2">
            <a href="/privacy" className="hover:text-slate-700">Privacy Policy</a>
            <span>•</span>
            <a href="/terms" className="hover:text-slate-700">Terms of Service</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfServicePage;
